import { useEffect, useRef, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router';
import { createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from 'store';
import { useGetArticleBySlugQuery } from 'store/services/blog/blogApi';
import ArticleLayout from 'screens/Auth/Login/Articles/ArticleLayout';
import { SEOHelmet, BreadcrumbList, StructuredData } from 'components/SEO';
import { Decklist, DecklistProps } from 'components/Decklist';
import {
  DecklistBatchResolver,
  ParsedDecklistData,
  ResolvedDecklistData,
} from 'components/DecklistBatchResolver';
import { CardLink } from 'components/CardLink';
import { transformCardLinks } from 'utils/cardLinkTransform';
import Footer from 'components/Footer';
import '../../../screens/Auth/Login/Articles/articles.css'; // Reuse existing article styles
import './DynamicArticle.css';

/**
 * Parse decklist data from HTML string before rendering
 * This allows us to batch-resolve all cards before hydration
 */
function parseDecklistsFromHtml(html: string): ParsedDecklistData[] {
  // Use DOMParser to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const decklistElements = doc.querySelectorAll('[data-type="decklist"]');

  const decklists: ParsedDecklistData[] = [];

  decklistElements.forEach((element, index) => {
    const htmlElement = element as HTMLElement;

    try {
      const title = htmlElement.dataset.title || 'Untitled Deck';
      const author = htmlElement.dataset.author || undefined;
      const format = htmlElement.dataset.format || undefined;
      const decklistId = htmlElement.dataset.id || `decklist-${index}`;

      let mainboard: DecklistProps['mainboard'] = [];
      if (htmlElement.dataset.mainboard) {
        try {
          mainboard = JSON.parse(htmlElement.dataset.mainboard);
        } catch (e) {
          console.error('Failed to parse mainboard data:', e);
        }
      }

      let sideboard: DecklistProps['sideboard'] = [];
      if (htmlElement.dataset.sideboard) {
        try {
          sideboard = JSON.parse(htmlElement.dataset.sideboard);
        } catch (e) {
          console.error('Failed to parse sideboard data:', e);
        }
      }

      decklists.push({
        title,
        author,
        format,
        id: decklistId,
        mainboard,
        sideboard: sideboard || [],
      });
    } catch (err) {
      console.error('Failed to parse decklist:', err);
    }
  });

  return decklists;
}

const DynamicArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isLoading, error } = useGetArticleBySlugQuery(slug || '');
  const contentRef = useRef<HTMLDivElement>(null);
  const decklistRootsRef = useRef<Map<HTMLElement, Root>>(new Map());
  const cardLinkRootsRef = useRef<Map<HTMLElement, Root>>(new Map());
  // Store resolved decklist data for hydration (using state so useEffect can depend on it)
  const [resolvedDecklists, setResolvedDecklists] = useState<ResolvedDecklistData[]>([]);
  const [decklistsLoading, setDecklistsLoading] = useState(true);

  // Set page title while loading
  useEffect(() => {
    if (article) {
      document.title = article.metaTitle || `${article.title} - TradeMagic`;
    }
  }, [article]);

  // Parse all decklists from article HTML before rendering
  // This allows batch resolution before hydration
  const parsedDecklists = useMemo(() => {
    if (!article?.contentHtml) return [];
    return parseDecklistsFromHtml(article.contentHtml);
  }, [article?.contentHtml]);

  // Hydrate decklist components with pre-resolved data
  const hydrateDecklists = (resolvedDecklists: ResolvedDecklistData[]) => {
    if (!contentRef.current) return;

    // Find all decklist placeholder divs
    const decklistElements = contentRef.current.querySelectorAll('[data-type="decklist"]');

    decklistElements.forEach((element, index) => {
      const htmlElement = element as HTMLElement;

      // Skip if already hydrated
      if (decklistRootsRef.current.has(htmlElement)) return;

      // Find matching resolved data by index (same order as parsing)
      const resolvedData = resolvedDecklists[index];
      if (!resolvedData) {
        console.error('No resolved data for decklist at index:', index);
        return;
      }

      try {
        // Create React root and render Decklist component with pre-resolved data
        const root = createRoot(htmlElement);
        decklistRootsRef.current.set(htmlElement, root);

        root.render(
          <Provider store={store}>
            <Decklist
              title={resolvedData.title}
              author={resolvedData.author}
              format={resolvedData.format}
              mainboard={resolvedData.mainboard}
              sideboard={resolvedData.sideboard}
              id={resolvedData.id}
              preResolvedData={resolvedData.preResolvedData}
            />
          </Provider>
        );
      } catch (err) {
        console.error('Failed to hydrate decklist:', err);
      }
    });
  };

  // Effect to hydrate decklists when resolved data is available
  useEffect(() => {
    if (!contentRef.current || !article) return;

    // Copy ref value for cleanup
    const rootsMap = decklistRootsRef.current;

    // If we have resolved decklists and they're done loading, hydrate them
    if (!decklistsLoading && resolvedDecklists.length > 0) {
      hydrateDecklists(resolvedDecklists);
    }

    // Cleanup function to unmount React roots
    return () => {
      rootsMap.forEach((root) => {
        root.unmount();
      });
      rootsMap.clear();
    };
  }, [article, resolvedDecklists, decklistsLoading]);

  // Hydrate card link components after content renders
  useEffect(() => {
    if (!contentRef.current || !article) return;

    // Copy ref value for cleanup
    const rootsMap = cardLinkRootsRef.current;

    // Find all card link placeholder spans
    const cardLinkElements = contentRef.current.querySelectorAll('.card-link-placeholder[data-card-name]');

    cardLinkElements.forEach((element) => {
      const htmlElement = element as HTMLElement;

      // Skip if already hydrated
      if (cardLinkRootsRef.current.has(htmlElement)) return;

      try {
        const cardName = htmlElement.dataset.cardName;
        if (!cardName) return;

        // Create React root and render CardLink component
        const root = createRoot(htmlElement);
        cardLinkRootsRef.current.set(htmlElement, root);

        root.render(
          <Provider store={store}>
            <CardLink cardName={cardName}>
              {cardName}
            </CardLink>
          </Provider>
        );
      } catch (err) {
        console.error('Failed to hydrate card link:', err);
      }
    });

    // Cleanup function to unmount React roots
    return () => {
      rootsMap.forEach((root) => {
        root.unmount();
      });
      rootsMap.clear();
    };
  }, [article]);

  // Loading state
  if (isLoading) {
    return (
      <div className="dynamic-article-loading">
        <div className="spinner-large"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  // Error / Not found state
  if (error || !article) {
    return <Navigate to="/articles" replace />;
  }

  // Check if article is published
  if (article.status !== 'PUBLISHED') {
    return <Navigate to="/articles" replace />;
  }

  const formatPublishedDate = (isoDateString: string): string => {
    const date = new Date(isoDateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Prepare canonical URL (lowercase, no trailing slash)
  const canonicalUrl = `https://trademagic.gg/articles/${article.slug.toLowerCase()}`;

  return (
    <>
      {/* SEO Components */}
      <SEOHelmet
        title={article.metaTitle || `${article.title} - TradeMagic`}
        description={article.metaDescription || article.subtitle || ''}
        canonical={canonicalUrl}
        image={article.ogImageUrl || article.heroImageUrl || 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share.png'}
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg' },
          { name: 'Articles', url: 'https://trademagic.gg/articles' },
          { name: article.title, url: canonicalUrl },
        ]}
      />

      <StructuredData
        data={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: article.title,
          description: article.metaDescription || article.subtitle || '',
          image: article.heroImageUrl || article.ogImageUrl,
          datePublished: article.publishedAt,
          dateModified: article.updatedAt,
          author: {
            '@type': 'Organization',
            name: 'TradeMagic',
          },
          publisher: {
            '@type': 'Organization',
            name: 'TradeMagic',
            logo: {
              '@type': 'ImageObject',
              url: 'https://trademagic.gg/logo.png',
            },
          },
        }}
      />

      {/* Batch resolve all decklists in article with a single API call */}
      <DecklistBatchResolver decklists={parsedDecklists}>
        {({ resolvedDecklists: resolved, isLoading: batchLoading }) => {
          // Update state when batch resolution completes
          // This triggers hydration via useEffect
          if (!batchLoading && resolved.length > 0 && resolvedDecklists.length === 0) {
            // Use setTimeout to avoid state update during render
            setTimeout(() => {
              setResolvedDecklists(resolved);
              setDecklistsLoading(false);
            }, 0);
          } else if (!batchLoading && parsedDecklists.length === 0 && decklistsLoading) {
            // No decklists in article - mark as done
            setTimeout(() => {
              setDecklistsLoading(false);
            }, 0);
          }

          return null; // DecklistBatchResolver doesn't render anything visible
        }}
      </DecklistBatchResolver>

      {/* Article Layout */}
      <ArticleLayout
        title={article.title}
        hero={article.heroImageUrl || ''}
      >
        <article className="dynamic-article-wrapper">
          {/* Subtitle */}
          {article.subtitle && (
            <p className="article-subtitle-text">{article.subtitle}</p>
          )}

          {/* Published date */}
          {article.publishedAt && (
            <p className="article-published-date">
              Published on {formatPublishedDate(article.publishedAt)}
            </p>
          )}

          {/* Article content (HTML from TipTap) */}
          {/* Transform [[Card Name]] patterns to interactive card links */}
          <div
            ref={contentRef}
            className="article-content"
            dangerouslySetInnerHTML={{ __html: transformCardLinks(article.contentHtml) }}
          />
        </article>

        <Footer />
      </ArticleLayout>
    </>
  );
};

export default DynamicArticle;
